import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  BookOpen,
  Save,
  RefreshCw,
  AlertCircle,
  ArrowLeft,
  ChevronDown,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const EditStudent = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [subjects, setSubjects] = useState([]);
  const [fetching, setFetching] = useState(true);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      gender: "male",
      address: "",
      city: "",
      state: "",
      pincode: "",
      enrollmentDate: "",
      selectedSubjects: [],
      guardianName: "",
      guardianPhone: "",
      guardianRelation: "father",
      previousSchool: "",
      profileImage: "",
    },
  });

  useEffect(() => {
    fetchSubjects();
    if (location.state?.student) {
      populateForm(location.state.student);
      setFetching(false);
    } else {
      fetchStudentDetails();
    }
  }, [id, location.state]);

  const populateForm = (student) => {
    const formattedData = {
      firstName: student.firstName || "",
      lastName: student.lastName || "",
      email: student.email || "",
      phone: student.phone || "",
      dateOfBirth: student.dateOfBirth ? student.dateOfBirth.split("T")[0] : "",
      gender: student.gender || "male",
      address: student.address || "",
      city: student.city || "",
      state: student.state || "",
      pincode: student.pincode || "",
      enrollmentDate: student.enrollmentDate
        ? student.enrollmentDate.split("T")[0]
        : "",
      guardianName: student.guardianName || "",
      guardianPhone: student.guardianPhone || "",
      guardianRelation: student.guardianRelation || "father",
      previousSchool: student.previousSchool || "",
      profileImage: student.profileImage || "",
      selectedSubjects: [],
    };

    if (student.selectedSubjects && Array.isArray(student.selectedSubjects)) {
      formattedData.selectedSubjects = student.selectedSubjects.map((sub) => {
        if (typeof sub === "object" && sub !== null) {
          return sub.id || sub._id;
        }
        return sub;
      });
    }

    reset(formattedData);
  };

  const fetchSubjects = async () => {
    try {
      const { data } = await axios.get(`${server}/subject/all`, {
        params: { pageSize: 100 },
      });
      setSubjects(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch subjects");
    }
  };

  const fetchStudentDetails = async () => {
    try {
      setFetching(true);
      // Assuming endpoint to get by ID exists or using all and find (safer to try fetch first if endpoint known, but falling back to all is existing pattern in EditTeacher)
      // Since StudentsList used query params for delete/update, maybe getById is similar?
      // Actually StudentsList doesn't have a getById call, it lists all.
      // Let's try to find it in the all list if individual fetch fails or isn't obvious.
      // Teacher page had `${server}/teachers/${id}`. Let's try `${server}/student/${id}` or similar if standardized.
      // But typically with these simplified backends, sometimes we just filter the list.

      // Let's try to fetch all and find, as it's robust enough for now if count isn't huge.
      const { data } = await axios.get(`${server}/student/all`, {
        params: { pageNo: 1, pageSize: 1000 }, // get enough to likely find it
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (data.data) {
        const student = data.data.find((s) => s.id == id || s._id == id);
        if (student) {
          populateForm(student);
        } else {
          toast.error("Student not found");
          navigate("/my/studentsList");
        }
      }
    } catch (error) {
      toast.error("Failed to fetch student details");
      navigate("/my/studentsList");
    } finally {
      setFetching(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        selectedSubjects: data.selectedSubjects.map((id) => Number(id)),
        dateOfBirth: new Date(data.dateOfBirth).toISOString(),
        enrollmentDate: new Date(data.enrollmentDate).toISOString(),
        profileImage: data.profileImage || "string",
      };

      await axios.put(`${server}/student/updateById`, payload, {
        params: { studentId: id },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      toast.success("Student updated successfully!");
      navigate("/my/studentsList");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update student");
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <motion.div
        className="max-w-5xl mx-auto space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/my/studentsList")}
            className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
          >
            <ArrowLeft className="w-5 h-5 text-gray-300" />
          </button>
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl">
                <User className="w-8 h-8 text-white" />
              </div>
              Edit Student
            </h1>
            <p className="text-gray-400 text-lg">Update student details</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Personal Information */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-400" />
              Personal Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                  {...register("firstName", {
                    required: "First name is required",
                  })}
                />
                {errors.firstName && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.firstName.message}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                  {...register("lastName", {
                    required: "Last name is required",
                  })}
                />
                {errors.lastName && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.lastName.message}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                  <Mail className="w-4 h-4" />
                  Email *
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                  <Phone className="w-4 h-4" />
                  Phone *
                </label>
                <input
                  type="tel"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                  {...register("phone", {
                    required: "Phone number is required",
                  })}
                />
                {errors.phone && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.phone.message}
                  </p>
                )}
              </div>

              {/* Date of Birth */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                  <Calendar className="w-4 h-4" />
                  Date of Birth *
                </label>
                <input
                  type="date"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors"
                  {...register("dateOfBirth", {
                    required: "Date of birth is required",
                  })}
                />
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Gender *
                </label>
                <div className="relative">
                  <select
                    className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors appearance-none"
                    {...register("gender")}
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-400" />
              Address Information
            </h2>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Address
                </label>
                <textarea
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors resize-none"
                  rows="3"
                  {...register("address")}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
                    {...register("city")}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
                    {...register("state")}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Pincode
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
                    {...register("pincode")}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Academic Information */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-400" />
              Academic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Enrollment Date *
                </label>
                <input
                  type="date"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                  {...register("enrollmentDate", {
                    required: "Enrollment date is required",
                  })}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Previous School
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  {...register("previousSchool")}
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-semibold text-gray-300 mb-3">
                Select Subjects
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-60 overflow-y-auto p-3 bg-gray-900 rounded-lg">
                {subjects.map((subject) => (
                  <label
                    key={subject.id}
                    className="flex items-center gap-2 p-2 hover:bg-gray-800 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      value={subject.id}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500"
                      {...register("selectedSubjects")}
                    />
                    <span className="text-sm text-gray-300">
                      {subject.subject}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Guardian Information */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-yellow-400" />
              Guardian Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Guardian Name *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors"
                  {...register("guardianName", {
                    required: "Guardian name is required",
                  })}
                />
                {errors.guardianName && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.guardianName.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Guardian Phone *
                </label>
                <input
                  type="tel"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors"
                  {...register("guardianPhone", {
                    required: "Guardian phone is required",
                  })}
                />
                {errors.guardianPhone && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.guardianPhone.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Relation
                </label>
                <div className="relative">
                  <select
                    className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-yellow-500 transition-colors appearance-none"
                    {...register("guardianRelation")}
                  >
                    <option value="father">Father</option>
                    <option value="mother">Mother</option>
                    <option value="guardian">Guardian</option>
                    <option value="other">Other</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 justify-end pt-4">
            <button
              type="button"
              onClick={() => navigate("/my/studentsList")}
              className="px-8 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors border border-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-8 py-3 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg hover:shadow-blue-500/20 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Update Student
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditStudent;
